import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Chat, TrendUp, Clock, Fire, WifiSlash } from '@phosphor-icons/react';
import { Container } from '@/components/common/Container';
import { Section } from '@/components/common/Section';
import { theme } from '@/styles/theme';
import { staggerContainer, fadeInUp } from '@/styles/animations';

const Title = styled.h2`
  text-align: center;
  margin-bottom: ${theme.spacing.xxxl};
  color: ${theme.colors.text.black};
`;

const BenefitsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.xl};
`;

const BenefitCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.xl};
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.large};
  box-shadow: ${theme.shadows.small};
  transition: all ${theme.transitions.normal};

  &:hover {
    box-shadow: ${theme.shadows.medium};
    transform: translateY(-4px);
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.medium};
  background: ${theme.colors.primary.extraLight};
  color: ${theme.colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BenefitTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.black};
  margin: 0;
`;

const BenefitDescription = styled.p`
  color: ${theme.colors.text.darkGray};
  margin: 0;
  font-size: ${theme.fontSizes.base};
  line-height: 1.6;
`;

const benefits = [
  {
    icon: <Chat size={28} weight="duotone" />,
    title: 'Express Yourself Better',
    description: 'Find the perfect word for every situation',
  },
  {
    icon: <TrendUp size={28} weight="duotone" />,
    title: 'Boost Confidence',
    description: 'Speak with precision and authority',
  },
  {
    icon: <Clock size={28} weight="duotone" />,
    title: 'Learn at Your Pace',
    description: 'No pressure, just steady progress',
  },
  {
    icon: <Fire size={28} weight="duotone" />,
    title: 'Daily Habit',
    description: '5 minutes leads to lasting growth',
  },
  {
    icon: <WifiSlash size={28} weight="duotone" />,
    title: 'Offline Ready',
    description: 'Practice anywhere, anytime',
  },
];

export const BenefitsSection = () => {
  return (
    <Section $padding="large" $background={theme.colors.secondary.warm}>
      <Container>
        <Title>More than just words. Build a richer life.</Title>
        <BenefitsGrid
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} variants={fadeInUp}>
              <IconWrapper>{benefit.icon}</IconWrapper>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitDescription>{benefit.description}</BenefitDescription>
            </BenefitCard>
          ))}
        </BenefitsGrid>
      </Container>
    </Section>
  );
};
